import type { Auth0Client } from '@auth0/auth0-spa-js'
import * as Sentry from '@sentry/react'
import { getLoginAuthorizationParams, isTransientAuthPath, markForceLogin, storeAuthRedirectUrl } from 'react-auth0'
import history from '../routing/history'
import type { AuthTokenResult } from './createAuthTokenGetter'

export const UNEXPECTED_ERROR_MESSAGE = 'An unexpected error occurred'

/**
 * Same-page-load idempotence for `recoverFromMissingToken`.
 *
 * One token failure can reach recovery twice in the same page: the `onAuthFailure` hook fires
 * inside `getAuthTokenResult`, and `AuthorizeUser`'s boot check then inspects the very same
 * settled result and calls recovery again. Without this flag the second call lands inside the
 * 2-minute `authRecoveryAt` window and escalates straight to /logout before the first redirect
 * has even navigated. The sessionStorage guard below keeps its separate job — stopping a loop
 * *across* the Auth0 round trip — which a module-level flag cannot do (the redirect is a full
 * page load).
 */
let recoveryStartedThisPageLoad = false

export function resetAuthRecoveryForTests() {
  recoveryStartedThisPageLoad = false
}

/**
 * Loop guard for the token-recovery login.
 *
 * `sessionStorage`, not `localStorage`, and deliberately so: the guard must survive the Auth0
 * redirect (a full page navigation, which `sessionStorage` does survive) but must NOT be shared
 * with other tabs. On `localStorage` a second tab opened during the window would read another
 * tab's attempt as its own, skip its recovery, and go straight to /logout.
 *
 * A timestamp, not the old `reloaded` boolean. `reloaded` was set before the reload and only
 * removed on the failure branch — never once a token was obtained — so after a single use it
 * stayed `'true'` for the rest of the profile's life and the next real failure skipped recovery
 * entirely. A timestamp self-expires, and `clearAuthRecoveryMark()` runs on every success.
 *
 * The window has to outlast a full Auth0 round trip (redirect, login form, callback, app boot) or
 * the guard would let a genuinely looping login through. Two minutes is comfortably longer than a
 * working login and far shorter than a session.
 */
export const AUTH_RECOVERY_KEY = 'authRecoveryAt'
export const AUTH_RECOVERY_WINDOW_MS = 2 * 60 * 1000

export function markAuthRecovery() {
  try {
    window.sessionStorage.setItem(AUTH_RECOVERY_KEY, String(Date.now()))
  } catch {
    // Storage can throw (Safari private browsing / partitioning). Without the mark the guard reads
    // "no recent attempt" and we may retry once more than intended — acceptable, and far better
    // than throwing inside the recovery path itself.
  }
}

export function clearAuthRecoveryMark() {
  try {
    window.sessionStorage.removeItem(AUTH_RECOVERY_KEY)
    // Retire the pre-ENG-1190 key so a profile carrying a stale `reloaded: 'true'` isn't left
    // with a dead flag forever.
    window.localStorage.removeItem('reloaded')
  } catch {
    // best effort
  }
}

export function recentlyAttemptedAuthRecovery() {
  try {
    const at = Number(window.sessionStorage.getItem(AUTH_RECOVERY_KEY))
    return !!at && Date.now() - at < AUTH_RECOVERY_WINDOW_MS
  } catch {
    return false
  }
}

export type AuthRecoveryOutcome = 'offline' | 'auth-route' | 'login-redirect' | 'logout'

export interface RecoverFromMissingTokenArgs {
  auth0Client: Pick<Auth0Client, 'loginWithRedirect'>
  push: (path: string, state?: unknown) => void
  error?: any
  timedOut?: boolean
}

/**
 * No token could be minted. Send the user through a real login instead of rendering an app that
 * cannot talk to the API.
 *
 * Covers every reason — timeout, `missing_refresh_token`, `invalid_grant` — not just a dead grant.
 * It also covers privileged users: `LoadData`'s appDocs error effect skips `isPrivileged`
 * accounts, so a superadmin whose token failed used to sit on the loading spinner with nothing to
 * click.
 *
 * `markForceLogin()` before `getLoginAuthorizationParams()` is what puts `prompt: 'login'` on the
 * request — without it Auth0's still-live SSO session silently re-issues the same broken identity
 * and we land right back here.
 *
 * Lives outside the component and takes `auth0Client`/`push` as arguments so it cannot close over
 * a stale render scope: the caller runs inside an effect whose deps do not include either, so an
 * in-component closure would keep the identities from the render that started the effect.
 */
export function recoverFromMissingToken({
  auth0Client,
  push,
  error,
  timedOut
}: RecoverFromMissingTokenArgs): AuthRecoveryOutcome {
  // Offline is not an auth failure. `getTokenSilently` cannot reach Auth0 with no network, so it
  // legitimately yields no token — and redirecting to a login form the browser cannot load would
  // strand an offline estimator who is otherwise working fine out of the IndexedDB cache. Leave
  // the app mounted and let it serve cached data; the next online load re-checks.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline'

  // The user is already on /logout, /login or /login-callback, so a login or logout navigation owns
  // the outcome — recovering here fights it and cannot help (ENG-1190).
  //
  // This is the real ENG-1190 case, not a hypothetical: click Log Out a second or two after a page
  // load and the token request that page fired is aborted by the navigation. Safari reports the
  // abort as `TypeError: Load failed`, which is not `invalid_grant`, so the auth-link hook stays
  // out of it — but `AuthorizeUser`'s boot check recovers on any missing token and still ran, from
  // an effect whose component had already unmounted. That recovery then stashed `/logout` as the
  // post-login return URL and sent the user through a login they had to repeat.
  //
  // `storeAuthRedirectUrl` refuses those paths too. Both guards are wanted: that one keeps a bad
  // value out of localStorage, this one keeps the redirect itself from firing.
  if (typeof window !== 'undefined' && isTransientAuthPath(window.location.pathname)) {
    // A breadcrumb, not a `captureException`. Every other path through this function reports to
    // Sentry, so returning in silence would erase the one signal that says whether this guard fires
    // in production — and that signal is how ENG-1190 was measured in the first place. A breadcrumb
    // costs no event quota and rides along on whatever the user hits next.
    Sentry.addBreadcrumb({
      category: 'auth',
      level: 'info',
      message: 'Auth recovery suppressed on a transient auth route',
      data: { pathname: window.location.pathname, timedOut: !!timedOut }
    })
    console.warn('[authRecovery] suppressed on', window.location.pathname)

    return 'auth-route'
  }

  // A recovery already started on this page load — a navigation (login redirect or /logout) is in
  // flight. Don't re-report or escalate. The 'login-redirect' outcome is nominal here; callers
  // don't branch on it (it exists for tests/telemetry).
  if (recoveryStartedThisPageLoad) return 'login-redirect'
  recoveryStartedThisPageLoad = true

  const loopGuarded = recentlyAttemptedAuthRecovery()

  // Tagged with the outcome so the loop-guard case is distinguishable in Sentry. Without the tag,
  // a guard-stopped recovery and a normal re-login produce identical events and there is no signal
  // that the guard is what ended the attempt.
  Sentry.captureException(error ?? new Error(timedOut ? 'Auth token timed out' : 'No auth token'), {
    tags: { message: 'Error with refresh token', authRecovery: loopGuarded ? 'loop-guarded' : 'login-redirect' },
    level: 'warning'
  })

  if (loopGuarded) {
    // A login round trip already happened inside the guard window and we still have no token, so
    // re-prompting would loop. Fall back to the explicit logout screen, which at least tells the
    // user something and gives them a way out.
    clearAuthRecoveryMark()
    push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
    return 'logout'
  }

  markAuthRecovery()
  markForceLogin()
  storeAuthRedirectUrl()
  auth0Client.loginWithRedirect({ authorizationParams: getLoginAuthorizationParams() }).catch((redirectError) => {
    // Without this the rejection is unhandled and the user is left on the spinner with nothing
    // explaining it. Fall back to the logout screen rather than retrying into the same failure.
    console.error('[AuthorizeUser] token recovery login failed', redirectError)
    Sentry.captureException(redirectError, { tags: { message: 'Token recovery login failed' } })
    push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
  })

  return 'login-redirect'
}

/**
 * Mid-session counterpart to the boot-time recovery above, wired into `createAuthTokenGetter`'s
 * `onAuthFailure` hook by each app's GraphQL client.
 *
 * Before this existed, a refresh token that died mid-session ("Unknown or invalid refresh token",
 * i.e. `invalid_grant`) had no recovery at all: the dead token stayed in the SDK cache, every
 * GraphQL operation retried it, went out with no Authorization header, and failed with
 * `jwt must be provided` — including saves — until the user logged out by hand (ENG-1190).
 *
 * Uses the shared history singleton (`shared/util/routing/history`): all three apps mount their
 * Router on it and all three have a /logout route, so the loop-guarded escalation works
 * everywhere without threading a `push` through the client modules.
 */
export function createAuthFailureHandler(
  auth0Client: Pick<Auth0Client, 'loginWithRedirect'>
): (result: AuthTokenResult) => void {
  return (result) => {
    // Belt-and-braces: `invalid_grant` implies Auth0 was reached, but the classification probes
    // are string matches — never redirect an offline user working out of the IndexedDB cache.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return

    // Never loginWithRedirect inside a frame: Auth0's universal login refuses to render framed,
    // leaving a blank stuck iframe (#19007). The app's iframe-notice path owns that UX.
    if (typeof window !== 'undefined' && window.self !== window.top) return

    recoverFromMissingToken({
      auth0Client,
      push: (path, state) => history.push(path, state),
      error: result.error,
      timedOut: result.timedOut
    })
  }
}
